import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, X } from 'lucide-react';

export interface CoeliacRef {
  title: string;
  url: string;
  summary: string;
}

export interface CoeliacAward {
  body: string;
  award?: string;
  url: string;
}

export const CoeliacRefTag: React.FC<{ refs: CoeliacRef[]; summary?: string }> = ({ refs, summary }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const tagRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isMobile) return;
    clearTimeout(hoverTimeout.current);
    setShowPopover(true);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (isMobile) return;
    hoverTimeout.current = setTimeout(() => setShowPopover(false), 200);
  }, [isMobile]);

  const handleClick = useCallback(() => {
    if (isMobile) setShowModal(true);
  }, [isMobile]);

  return (
    <div className="coeliac-ref-wrapper">
      <button
        ref={tagRef}
        className="coeliac-ref-tag"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        aria-label="View coeliac references"
      >
        <ShieldCheck size={11} />
        <span>GF Reviewed</span>
      </button>
      {showPopover && !isMobile && (
        <div
          ref={popoverRef}
          className="coeliac-popover"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="coeliac-popover-header">
            <ShieldCheck size={14} />
            <span>Coeliac References</span>
          </div>
          {summary && <p className="coeliac-summary">{summary}</p>}
          <div className="coeliac-popover-list">
            {refs.map((ref, i) => (
              <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="coeliac-ref-card">
                <span className="coeliac-ref-card-source">{ref.title}</span>
                <span className="coeliac-ref-card-summary">{ref.summary}</span>
              </a>
            ))}
          </div>
        </div>
      )}
      <AnimatePresence>
        {showModal && isMobile && (
          <motion.div
            className="coeliac-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="coeliac-modal"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="coeliac-modal-header">
                <ShieldCheck size={16} />
                <span>Coeliac References</span>
                <button className="coeliac-modal-close" onClick={() => setShowModal(false)}>
                  <X size={18} />
                </button>
              </div>
              {summary && <p className="coeliac-summary">{summary}</p>}
              <div className="coeliac-modal-list">
                {refs.map((ref, i) => (
                  <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="coeliac-ref-card">
                    <span className="coeliac-ref-card-source">{ref.title}</span>
                    <span className="coeliac-ref-card-summary">{ref.summary}</span>
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const CoeliacAwardTag: React.FC<{ award: CoeliacAward }> = ({ award }) => (
  <a href={award.url} target="_blank" rel="noopener noreferrer" className="coeliac-uk-tag">
    <span className="coeliac-uk-tag-badge">{award.body}</span>
    <span className="coeliac-uk-tag-label">{award.award || 'Recognised'}</span>
  </a>
);
